import { getPageSuggestions } from 'components/Chat/instructor-hugo/pageConfig';
import { useAuth } from 'contexts/auth';
import { useWebsocket } from 'contexts/websocket';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatRequestLifecycleEvent } from 'utils/chat/chatConstants';
import { buildSessionContext } from 'utils/chat/instructorHugoContext';
import {
    buildInstructorHugoLoadingStatusSpecs,
    selectInstructorHugoLoadingTitle,
} from 'utils/chat/instructorHugoLoading';
import {
    createLocalChatShell,
    getChatTitle,
    getFirstName,
    getRouteChatKey,
    shouldShowPageSuggestions,
} from './useInstructorHugoChatHelpers';
import useInstructorHugoChatContext from './useInstructorHugoContext';
import useInstructorHugoMessageLifecycle from './useInstructorHugoMessageLifecycle';
import useInstructorHugoSync from './useInstructorHugoSync';
import useInstructorHugoThreads from './useInstructorHugoThreads';

const useInstructorHugoChat = ({ isOpen, pathname }) => {
    const { auth, currentUser } = useAuth();
    const { t } = useTranslation('chat');
    const { isConnected, onMessage, setPingPayload } = useWebsocket();

    const [sessionContext, setSessionContext] = useState(() => buildSessionContext({ auth, pathname }));
    const { currentRouteChatKey, derivedSessionContext, outgoingContext, outgoingPageContext, scope } =
        useInstructorHugoChatContext({
            auth,
            pathname,
            sessionContext,
        });
    const [activeRouteChatKey, setActiveRouteChatKey] = useState(() =>
        getRouteChatKey({ auth, sessionContext: buildSessionContext({ auth, pathname }) })
    );
    const [chat, setChat] = useState(() => createLocalChatShell({ auth, scope: sessionContext.scope }));
    const [loadingActivityRequest, setLoadingActivityRequest] = useState(null);
    const isChatRouteSynced = activeRouteChatKey === currentRouteChatKey;
    const chatRef = useRef(chat);

    const threadControls = useInstructorHugoThreads({
        auth,
        chat,
        chatRef,
        scope,
        setChat,
        t,
    });
    const { bindLocalChatToPersistedChat, loadThreadSummaries, onDeleteThread, threadListAdapter, threadSwitcher } =
        threadControls;

    const { reconcilePersistedAssistantMessage, refreshActiveThread, setRuntimeBlocksReconciliation } =
        useInstructorHugoSync({
            chat,
            chatRef,
            isConnected,
            isOpen,
            loadThreadSummaries,
            onMessage,
            setChat,
            setPingPayload,
        });

    const { buildOutgoingRequest, onChatRequestStateChange: onLifecycleStateChange } =
        useInstructorHugoMessageLifecycle({
            bindLocalChatToPersistedChat,
            chatRef,
            loadThreadSummaries,
            outgoingContext,
            outgoingPageContext,
            reconcilePersistedAssistantMessage,
            refreshActiveThread,
            scope,
            setChat,
            setRuntimeBlocksReconciliation,
        });

    const onChatRequestStateChange = useCallback(
        (event) => {
            if (event?.type === ChatRequestLifecycleEvent.REQUEST_STARTED) {
                setLoadingActivityRequest({
                    context: outgoingContext,
                    pageContext: outgoingPageContext,
                    requestSalt: event.requestSalt,
                    requestStartedAt: event.requestStartedAt,
                    requestType: event.requestType,
                    scope,
                });
            }

            if (
                event?.type === ChatRequestLifecycleEvent.ASSISTANT_MESSAGE_CREATED ||
                event?.type === ChatRequestLifecycleEvent.REQUEST_FAILED
            ) {
                setLoadingActivityRequest(null);
            }

            onLifecycleStateChange(event);
        },
        [onLifecycleStateChange, outgoingContext, outgoingPageContext, scope]
    );

    useEffect(() => {
        if (!isOpen) return;

        loadThreadSummaries();

        if (isChatRouteSynced) return;

        setActiveRouteChatKey(currentRouteChatKey);
        setSessionContext(derivedSessionContext);
        setChat(createLocalChatShell({ auth, scope: derivedSessionContext.scope }));
    }, [auth, currentRouteChatKey, derivedSessionContext, isChatRouteSynced, isOpen, loadThreadSummaries]);

    const pageSuggestions = useMemo(
        () => getPageSuggestions(outgoingPageContext.page_type, t),
        [outgoingPageContext.page_type, t]
    );
    const loadingActivity = useMemo(() => {
        if (!loadingActivityRequest) return null;

        return {
            requestSalt: loadingActivityRequest.requestSalt,
            requestStartedAt: loadingActivityRequest.requestStartedAt,
            requestType: loadingActivityRequest.requestType,
            getActivity: (salt, { requestType = null } = {}) => {
                const title = selectInstructorHugoLoadingTitle({ salt });

                return {
                    statuses: buildInstructorHugoLoadingStatusSpecs({
                        context: loadingActivityRequest.context,
                        pageContext: loadingActivityRequest.pageContext,
                        requestType,
                        salt,
                        scope: loadingActivityRequest.scope,
                    }).map(({ key, defaultValue, values }) => t(`hugo.loading.${key}`, defaultValue, values)),
                    title: t(`hugo.loading.${title.key}`, title.defaultValue),
                };
            },
        };
    }, [loadingActivityRequest, t]);
    const showPageSuggestions = shouldShowPageSuggestions(chat);

    return {
        buildOutgoingRequest,
        chat,
        isChatRouteSynced,
        isBootstrapping: false,
        onChatRequestStateChange,
        onDeleteThread,
        onRefreshThreads: loadThreadSummaries,
        showSuggestions: showPageSuggestions,
        suggestions: pageSuggestions,
        suggestionsTitle: t('hugo.quick-ideas-title', 'Quick ideas to get you started'),
        threadListAdapter,
        threadSwitcher,
        viewModel: {
            composer: {
                placeholder: t('hugo.placeholder-label', 'Help me with this page'),
            },
            emptyState: {
                body: t('hugo.greeting-assignment-message', 'How can I help with your assignment?'),
                firstName: getFirstName({ auth, currentUser }),
                isVisible: showPageSuggestions,
            },
            header: {
                title: getChatTitle(chat) || t('hugo.title', 'Hugo'),
            },
            loadingActivity,
        },
        assistantIdentity: {
            icon: 'hugo',
            label: t('hugo.title', 'Hugo'),
        },
    };
};

export default useInstructorHugoChat;
